import { useState, useEffect, useRef } from "react";
import { Send, Save, AlertTriangle, ChevronDown, Search, Check, ChevronRight } from "lucide-react";
import { useContentStore } from "@/store/useContentStore";
import { routeInstruction } from "../lib/intentRouter";

/* ─── Model catalogue ───────────────────────────────────────────── */

interface Model {
  id: string;
  name: string;
  badge?: string;
}

const MODELS: Model[] = [
  { id: "claude-sonnet-4-6", name: "Claude Sonnet 4.6", badge: "Best" },
  { id: "claude-3-5-haiku-20241022",  name: "Claude 3.5 Haiku",  badge: "Fast" },
  { id: "gpt-4o",                     name: "GPT-4o" },
  { id: "gemini-2.0-flash",           name: "Gemini 2.0 Flash" },
  { id: "o1-preview",                 name: "o1-preview",        badge: "Brain" },
];

const DEFAULT_MODEL = MODELS[0];

/* ─── Props (minimal — most state comes from the store) ────────── */

export interface RightAgentPanelProps {
  /** Complex async handler that lives in page.tsx (involves API calls) */
  onSelectTitle: (title: string) => void;
  /** Save-to-hub handler lives in page.tsx (needs setArticles) */
  onSave: () => void;
}

/* ─── Markdown-lite: **bold** → <strong> ───────────────────────── */

function renderContent(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i}>{p.slice(2, -2)}</strong>
    ) : (
      p
    )
  );
}

export default function RightAgentPanel({
  onSelectTitle,
  onSave,
}: RightAgentPanelProps) {
  /* ── Read from global store (selective subscriptions → no extra re-renders) */
  const messages   = useContentStore((s) => s.agentMsgs);
  const stage      = useContentStore((s) => s.stage);
  const isBusy     = useContentStore((s) => s.isAgentBusy);
  const violations = useContentStore((s) => s.violations);

  /* ── Store actions */
  const pushAgentMsg  = useContentStore((s) => s.pushAgentMsg);
  const setAgentBusy  = useContentStore((s) => s.setAgentBusy);

  /* ── Context snapshot for the intent router */
  const keywords        = useContentStore((s) => s.keywords);
  const outline         = useContentStore((s) => s.outline);
  const articleHtml     = useContentStore((s) => s.articleHtml);
  const bannerCopy      = useContentStore((s) => s.bannerCopy);
  const videoScriptCopy = useContentStore((s) => s.videoScriptCopy);

  /* ── Local UI state (no need to be global) */
  const [lang, setLang]   = useState<"id" | "zh">("id");
  const [input, setInput] = useState("");
  const [isFixing, setIsFixing] = useState(false);
  const bottomRef         = useRef<HTMLDivElement>(null);

  /* ── Model selector state */
  const [selectedModel, setSelectedModel] = useState<Model>(DEFAULT_MODEL);
  const [selectorOpen, setSelectorOpen]   = useState(false);
  const [searchQuery, setSearchQuery]     = useState("");
  const [maxMode, setMaxMode]             = useState(false);
  const selectorRef = useRef<HTMLDivElement>(null);

  /* Close selector on outside click */
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (selectorRef.current && !selectorRef.current.contains(e.target as Node)) {
        setSelectorOpen(false);
      }
    }
    if (selectorOpen) document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [selectorOpen]);

  /* Filtered model list */
  const filteredModels = MODELS.filter((m) =>
    m.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  /* Auto-scroll to latest message */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isBusy]);

  /* ── Send handler: calls intentRouter, dispatches to store ── */
  /**
   * @param overrideText  When provided (e.g. from a QuickChip click), this text
   *                      is used directly instead of reading from the `input` state.
   *                      This is essential to avoid the stale-closure bug where
   *                      setTimeout-based callers hold onto an old `input` value of "".
   */
  async function handleSend(overrideText?: string) {
    const txt = (overrideText ?? input).trim();
    if (!txt || isBusy) return;

    /* Always clear the text box regardless of whether override was used */
    setInput("");

    /* 1. Show user bubble immediately */
    pushAgentMsg({ role: "user", content: txt });

    /* 2. Show typing indicator */
    setAgentBusy(true);

    try {
      /* 3. Route instruction through LLM → tool dispatch → store action.
            Pass full workflow context so the LLM can reference existing content. */
      const reply = await routeInstruction(
        txt,
        { stage, keywords, outline, articleHtml, bannerCopy, videoScriptCopy },
        selectedModel.id
      );

      /* 4. Show agent reply in chat */
      pushAgentMsg({ role: "agent", content: reply });
    } finally {
      setAgentBusy(false);
    }
  }

  /* ── One-click OJK compliance fixer ─────────────────────────── */
  /**
   * Builds a targeted rewrite instruction from the current violations list
   * and sends it through the same LLM → tool-call → store pipeline as any
   * other Agent instruction.  The `update_article` dispatch in intentRouter
   * will simultaneously rewrite the article AND clear the violations list.
   */
  async function handleFixCompliance() {
    if (isFixing || isBusy || violations.length === 0) return;

    setIsFixing(true);
    try {
      const violationList = violations
        .map((v, i) => `${i + 1}. ${v}`)
        .join("\n");

      const fixInstruction =
        `You are an OJK compliance editor. The article currently contains the following violations:\n\n` +
        `${violationList}\n\n` +
        `Task: Rewrite the COMPLETE article to remove or replace every restricted term with OJK-compliant, ` +
        `legally safe language. Preserve the original structure, tone, length, and Bahasa Indonesia. ` +
        `Do NOT add disclaimers as separate paragraphs — weave the corrections into the existing text naturally. ` +
        `Call the update_article tool with the full corrected HTML content.`;

      await handleSend(fixInstruction);
    } finally {
      setIsFixing(false);
    }
  }

  return (
    <aside className="w-[340px] flex-shrink-0 h-full bg-white border-l border-gray-100 flex flex-col">

      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
        <p className="text-sm font-bold text-gray-800">AI Editorial Agent</p>
        <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5 text-xs font-semibold">
          {(["id", "zh"] as const).map((l) => (
            <button
              key={l}
              onClick={() => setLang(l)}
              className={`px-2.5 py-1 rounded-md uppercase transition-colors ${
                lang === l
                  ? "bg-white text-gray-800 shadow-sm"
                  : "text-gray-400 hover:text-gray-600"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* ── Compliance alert ─────────────────────────────────────── */}
      {violations.length > 0 && (
        <div className="mx-4 mt-3 bg-red-50 border border-red-200 rounded-2xl p-4 flex-shrink-0">
          <div className="flex items-center gap-2 text-red-600 font-bold text-xs mb-2">
            <AlertTriangle size={13} />
            COMPLIANCE VIOLATION
          </div>
          {violations.map((v, i) => (
            <p key={i} className="text-xs text-red-700 leading-relaxed">{v}</p>
          ))}
          <button
            onClick={handleFixCompliance}
            disabled={isFixing || isBusy}
            className="mt-2 w-full flex items-center justify-center gap-1.5 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl transition-colors"
          >
            {isFixing ? (
              <>
                <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin flex-shrink-0" />
                Fixing…
              </>
            ) : (
              "⚡ Fix Now"
            )}
          </button>
        </div>
      )}

      {/* ── Message stream ───────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.map((msg) => (
          <div key={msg.id}>
            {msg.role === "agent" ? (
              <div className="flex flex-col gap-2">
                {/* Agent bubble */}
                <div className="bg-gray-50 border border-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 text-xs text-gray-700 leading-relaxed max-w-[90%]">
                  {renderContent(msg.content)}
                </div>

                {/* Title suggestion cards */}
                {msg.titles && msg.titles.length > 0 && (
                  <div className="space-y-2 mt-1">
                    {msg.titles.map((title, i) => (
                      <button
                        key={i}
                        onClick={() => onSelectTitle(title)}
                        className="w-full text-left bg-white border border-gray-200 hover:border-indigo-400 hover:bg-indigo-50 rounded-xl px-4 py-3 transition-all group"
                      >
                        <div className="flex items-start gap-2">
                          <span className="text-[10px] font-bold text-indigo-400 bg-indigo-50 group-hover:bg-white rounded-md px-1.5 py-0.5 flex-shrink-0 mt-0.5">
                            T{i + 1}
                          </span>
                          <p className="text-xs font-semibold text-gray-800 leading-snug group-hover:text-indigo-700 transition-colors">
                            {title}
                          </p>
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1.5 ml-6 group-hover:text-indigo-500">
                          Click to use this title →
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* User bubble */
              <div className="flex justify-end">
                <div className="bg-indigo-600 rounded-2xl rounded-tr-sm px-4 py-3 text-xs text-white leading-relaxed max-w-[85%]">
                  {msg.content}
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Typing indicator */}
        {isBusy && (
          <div className="flex items-center gap-1.5 px-4 py-3 bg-gray-50 border border-gray-100 rounded-2xl rounded-tl-sm w-fit">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce"
                style={{ animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </div>
        )}

        {/* Stage-specific quick-action chips */}
        {stage === "outline" && !isBusy && (
          <QuickChips
            chips={["Make it shorter", "Add more sections", "Translate to ZH"]}
            onSend={handleSend}
          />
        )}
        {(stage === "writing" || stage === "done") && !isBusy && (
          <QuickChips
            chips={[
              "Make intro more engaging",
              "Add SEO FAQ section",
              "Write the TikTok script",
              "Write banner copy",
            ]}
            onSend={handleSend}
          />
        )}

        <div ref={bottomRef} />
      </div>

      {/* ── Save button (visible after article is ready) ─────────── */}
      {(stage === "writing" || stage === "done") && (
        <div className="px-4 pb-3 flex-shrink-0">
          <button
            onClick={onSave}
            className="w-full flex items-center justify-center gap-2 py-3 bg-indigo-700 hover:bg-indigo-800 text-white text-sm font-bold rounded-2xl transition-colors shadow-lg shadow-indigo-900/20"
          >
            <Save size={14} />
            Save to Content Hub
          </button>
        </div>
      )}

      {/* ── Model selector + Instruction input ───────────────────── */}
      <div className="px-4 pb-4 flex-shrink-0 border-t border-gray-100 pt-3 space-y-2">

        {/* Model selector trigger + popover */}
        <div ref={selectorRef} className="relative z-[200]">
          {/* Trigger button */}
          <button
            onClick={() => { setSelectorOpen((v) => !v); setSearchQuery(""); }}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-xs font-semibold text-gray-700"
          >
            <span className="max-w-[140px] truncate">{selectedModel.name}</span>
            <ChevronDown size={12} className={`transition-transform ${selectorOpen ? "rotate-180" : ""}`} />
          </button>

          {/* Popover — opens upward */}
          {selectorOpen && (
            <div className="absolute bottom-full z-[210] mb-2 left-0 w-72 rounded-xl border border-gray-200 bg-white/90 backdrop-blur-md shadow-lg overflow-hidden">

              {/* Search */}
              <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100">
                <Search size={13} className="text-gray-400 flex-shrink-0" />
                <input
                  autoFocus
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search models..."
                  className="flex-1 text-xs text-gray-700 bg-transparent placeholder-gray-400 focus:outline-none"
                />
              </div>

              {/* MAX Mode toggle */}
              <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                <span className="text-xs font-semibold text-gray-700">MAX Mode</span>
                {/* iOS-style toggle */}
                <button
                  onClick={() => setMaxMode((v) => !v)}
                  className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${
                    maxMode ? "bg-indigo-600" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
                      maxMode ? "translate-x-4" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>

              {/* Model list */}
              <div className="max-h-52 overflow-y-auto py-1">
                {filteredModels.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-4">No models found</p>
                ) : (
                  filteredModels.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => { setSelectedModel(m); setSelectorOpen(false); }}
                      className={`w-full flex items-center justify-between px-3 py-2 text-left transition-colors ${
                        selectedModel.id === m.id
                          ? "bg-indigo-50 text-indigo-700"
                          : "hover:bg-gray-50 text-gray-700"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium">{m.name}</span>
                        {m.badge && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-indigo-100 text-indigo-600">
                            {m.badge}
                          </span>
                        )}
                      </div>
                      {selectedModel.id === m.id && (
                        <Check size={13} className="text-indigo-600 flex-shrink-0" />
                      )}
                    </button>
                  ))
                )}
              </div>

              {/* Footer: Add Models */}
              <div className="border-t border-gray-100">
                <button className="w-full flex items-center justify-between px-3 py-2 text-xs text-gray-500 hover:bg-gray-50 transition-colors">
                  <span className="font-medium">Add Models</span>
                  <ChevronRight size={13} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Chat input */}
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-2xl px-4 py-2.5 focus-within:border-indigo-300 focus-within:ring-1 focus-within:ring-indigo-100 transition-all">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) handleSend();
              // no override needed — keyboard Enter reads from `input` state directly
            }}
            placeholder={
              stage === "keyword"
                ? "Ask a question..."
                : stage === "outline"
                ? "Give outline instructions..."
                : "Refine the article..."
            }
            className="flex-1 bg-transparent text-sm text-gray-700 placeholder-gray-400 focus:outline-none"
          />
          <button
            onClick={() => handleSend()}
            disabled={!input.trim() || isBusy}
            className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${
              input.trim() && !isBusy
                ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            <Send size={12} />
          </button>
        </div>
        <p className="text-[10px] text-gray-400 px-1">
          Agent can rewrite outline, article, banner copy, or video script.
        </p>
      </div>
    </aside>
  );
}

/* ─── Quick-chip helper ─────────────────────────────────────────── */

function QuickChips({
  chips,
  onSend,
}: {
  chips: string[];
  /**
   * Called with the chip text directly — no `setInput` + `setTimeout` indirection,
   * which previously caused a stale-closure bug where handleSend read `input = ""`.
   */
  onSend: (text: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {chips.map((chip) => (
        <button
          key={chip}
          onClick={() => onSend(chip)}
          className="text-[11px] px-2.5 py-1 bg-gray-100 hover:bg-indigo-100 hover:text-indigo-700 text-gray-500 rounded-lg transition-colors"
        >
          {chip}
        </button>
      ))}
    </div>
  );
}
